// 思考室（lingzhi-studio）。和灵智原生同源、同一个登录令牌。
import { request } from './client'

const BASE = '/studio/api/think'

export type ThinkPhase = 'forming' | 'positions_ready' | 'clashing' | 'probing' | 'awaiting_final' | 'closing' | 'closed' | 'failed'
export const THINK_RUNNING: ThinkPhase[] = ['forming', 'clashing', 'probing', 'closing']

export interface ThinkEvidence {
  n: number
  knowledgeId: string
  kbId: string
  title: string
  text: string
  kind: 'passage' | 'attached' | 'verdict'
}
export interface ThinkDebater {
  name: string
  stance: string
  anchors: number[]
  past?: boolean
}
export interface ThinkStatement {
  debater: string
  round: number
  kind: 'position' | 'rebuttal'
  target?: string
  text: string
  cites: number[]
}
export interface ThinkInterjection {
  text: string
  atRound: number
  target?: string
  at: string
}
export interface ThinkSavedDoc {
  knowledgeId: string
  kbId: string
  title: string
}
export interface ThinkSession {
  id: string
  topic: string
  kbIds: string[]
  saveKbId: string
  contextKnowledgeIds: string[]
  phase: ThinkPhase
  evidence: ThinkEvidence[]
  debaters: ThinkDebater[]
  writing: string[]
  statements: ThinkStatement[]
  interjections: ThinkInterjection[]
  clashRound: number
  verdictDraft?: string
  probe?: string
  verdict?: string
  saved?: { map?: ThinkSavedDoc; verdict?: ThinkSavedDoc }
  error?: string
  failedStep?: 'forming' | 'clashing' | 'probing' | 'closing'
  calls: number
  createdAt: string
  updatedAt: string
}
export interface ThinkSummary {
  id: string
  topic: string
  phase: ThinkPhase
  clashRound: number
  createdAt: string
  updatedAt: string
}
export interface ThinkMeta {
  limits: { maxContext: number; maxClashRounds: number; topicChars: number; createsPerHour: number; activeSessions: number }
  calls: { forming: string; clashRound: string; closing: number }
  folder: string
}

const one = (p: Promise<{ data: ThinkSession }>) => p.then((r) => r.data)

export const thinkMeta = () => request<ThinkMeta>(`${BASE}/meta`)
export const listThinks = () => request<{ data: ThinkSummary[] }>(BASE).then((r) => r.data ?? [])
export const getThink = (id: string) => one(request(`${BASE}/${id}`))
export const createThink = (input: { topic: string; knowledgeBaseIds?: string[]; contextKnowledgeIds?: string[] }) => one(request(BASE, { body: input }))
export const clashThink = (id: string, force = false) => one(request(`${BASE}/${id}/clash`, { body: { force } }))
export const interjectThink = (id: string, text: string, target?: string) => one(request(`${BASE}/${id}/interject`, { body: { text, target } }))
export const verdictThink = (id: string, draft: string) => one(request(`${BASE}/${id}/verdict`, { body: { draft } }))
export const finalThink = (id: string, verdict: string) => one(request(`${BASE}/${id}/final`, { body: { verdict } }))
export const retryThink = (id: string) => one(request(`${BASE}/${id}/retry`, { body: {} }))
export const deleteThink = (id: string) => request(`${BASE}/${id}`, { method: 'DELETE' })

/** 进行到哪一步（网页上的四步）。 */
export function thinkStep(s: Pick<ThinkSession, 'phase' | 'failedStep' | 'verdictDraft'>): 0 | 1 | 2 | 3 | 4 {
  const phase = s.phase === 'failed' ? s.failedStep ?? 'forming' : s.phase
  switch (phase) {
    case 'forming':
      return 0
    case 'positions_ready':
    case 'clashing':
      return 1
    case 'probing':
    case 'awaiting_final':
      return 2
    case 'closing':
      return 3
    case 'closed':
      return 4
    default:
      return 1
  }
}

export const PHASE_LABEL: Record<ThinkPhase, string> = {
  forming: '正在组建视角',
  positions_ready: '比较立场 · 等你出招',
  clashing: '正在交锋',
  probing: '教练在看你的倾向',
  awaiting_final: '做出判断 · 等你定稿',
  closing: '正在存档',
  closed: '已存档',
  failed: '中断了 · 可以重试',
}
